import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import HeadingCard from "../../../common/element/Card/HeadingCard";
import Sidebar from "./Sidebar";
import StepComponent from "./StepComponent";
import ResultCard from "./ResultCard";
import { useDispatch, useSelector } from "react-redux";
// ✅ Added checkTokenExpiry to imports
import {
  fetchPersonalityTestOptions,
  submitPersonalityTest,
  checkTokenExpiry,
} from "../../../redux/tool/toolSlice";
import Footer from "../Footer";
import Button from "../../../common/element/Buttons";
import SweetAlertService from "../../../common/SweetAlert/SweetAlert";
// ✅ Import Expired Page
import ExpiredTokenPage from "../ExpiredTokenPage";
import ThreeDotBouncingLoader from "../../../common/element/ButtonLoader/ThreeDotBouncingLoder";
import InvalidTokenPage from "../InvalidToken";
import LoginFormFORprospect from "../LoginFormFORprospect";
import { submitSessionUrl } from "../../../redux/SessionUrl/sessionUrlSlice";

const TOTAL_STEPS = 8;
const QUESTIONS_PER_STEP = 18;

// Start Screen Component
const StartScreen = ({ onStart }) => {
  return (
    <>
      <HeadingCard title="Instructions">
        <ul className="space-y-4">
          {[
            `In this Assessment, you will see 144 questions. Each question has two options "a" & "b".`,
            `Select the option which truly reflects you instead of what seems to be right.`,
          ].map((text, i) => (
            <li
              key={i}
              className="flex items-start md:items-center lg:items-center gap-3"
            >
              <span className="flex-shrink-0 w-8 h-8 bg-[#FF8E1C] rounded-full font-lato not-italic font-semibold text-[14px] leading-[17px] text-white flex items-center justify-center mt-1">
                {i + 1}
              </span>
              <h3 className="font-lato not-italic font-bold text-[16px] md:text-[20px] lg:text-[20px] mobile_fix  text-start lg:text-center leading-[115%] text-black">
                {text}
              </h3>
            </li>
          ))}
        </ul>
      </HeadingCard>
      <div className="flex justify-center">
        <button
          onClick={onStart}
          className="mt-[32px]  w-[168px] h-[48px] rounded-[12px] text-white font-lato font-semibold text-[16px] leading-[115%] bg-[#FF8E1C] border border-[#FFF4E5]/50 shadow-[0px_2px_5px_#FFF4E5]"
        >
          START THIS TEST
        </button>
      </div>
    </>
  );
};

const PersonalityTest = () => {
  const [searchParams] = useSearchParams();

  // Clear prospect token from localStorage on mount (handles refresh)
  useEffect(() => {
    try {
      localStorage.removeItem('prospect_token');
    } catch (e) { }
  }, []);

  const token = searchParams.get("token");
  // Prospect/company params from route
  const companyIdParam = searchParams.get("company_id");
  const companyNameParam = searchParams.get("company_name");
  const companySessionIdParam = searchParams.get("company_session_id");
  const sessionNameParam = searchParams.get("session_name");
  const sessionTypeParam = searchParams.get("session_type");
  const isSessionUrlFlow = sessionTypeParam === "session_url";
  const isInterviewCandidate = searchParams.get("is_interview_candidate") === "true";
  const isProspect = searchParams.get("is_prospect") === "true";

  const [prospectRequired] = useState(
    Boolean(companyIdParam && companyNameParam),
  );
  const [prospectConfirmed, setProspectConfirmed] = useState(false);
  const storedProspectToken = (() => { try { return localStorage.getItem('prospect_token'); } catch (e) { return null; } })();
  const [prospectToken, setProspectToken] = useState(storedProspectToken || null);
  const [sessionUrlUserData, setSessionUrlUserData] = useState(null);

  const [hasStarted, setHasStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState([]);
  const [linkToken, setLinkToken] = useState(null);
  const [selections, setSelections] = useState(() => {
    const obj = {};
    for (let i = 1; i <= TOTAL_STEPS; i++) obj[i] = {};
    return obj;
  });
  const [completed, setCompleted] = useState([]);

  const dispatch = useDispatch();

  // ✅ Extract tokenExpiryCheck from Redux
  const { personalityOptions, personalitySubmit, tokenExpiryCheck } =
    useSelector((state) => state.tool);

  const questions = personalityOptions?.data?.questions || [];
  const loading = personalityOptions?.loading;

  // ✅ Token Expiry Data
  const tokenExpiryData = tokenExpiryCheck?.data;
  const tokenExpiryLoading = tokenExpiryCheck?.loading;

  console.log("personalitySubmit from Redux:", personalitySubmit);
  console.log("Token from URL:", token);

  useEffect(() => {
    if (!prospectRequired || prospectConfirmed) {
      dispatch(fetchPersonalityTestOptions());
    }
  }, [dispatch, prospectRequired, prospectConfirmed]);

  // ✅ Check Token Expiry on mount (route token)
  useEffect(() => {
    if (!token) {
      // If this is a prospect/company flow we do not show the missing-token error
      if (!prospectRequired) {
        SweetAlertService.showError({
          title: "Error!",
          message:
            "Invalid or missing token. Please use a valid link to access the test.",
          onConfirm: () => { },
        });
      }
    } else {
      // Only check token expiry on mount when NOT in prospect/company flow
      if (!prospectRequired) {
        dispatch(checkTokenExpiry(token));
      }
    }
  }, [token, dispatch, prospectRequired]);

  const handleChange = (step) => (nextQuestionMap) => {
    console.log(`🔄 Step ${step} Updated:`, nextQuestionMap);
    setSelections((s) => {
      const updated = { ...s, [step]: nextQuestionMap };
      console.log("📋 All Selections:", updated);
      return updated;
    });
  };

  const attemptedCount = Object.values(selections).reduce((sum, stepMap) => {
    return (
      sum +
      Object.values(stepMap || {}).filter((v) => v !== null && v !== undefined)
        .length
    );
  }, 0);

  const totalQuestions = TOTAL_STEPS * QUESTIONS_PER_STEP;

  const handleNext = () => {
    if (!completed.includes(currentStep)) {
      setCompleted((c) => [...c, currentStep]);
    }
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCompleted((c) => c.filter((step) => step !== currentStep));
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSelectStep = (step) => {
    if (hasStarted && !showResults) {
      setCurrentStep(step);
    }
  };

  const handleStart = () => {
    if (!isSessionUrlFlow) {
      const effectiveToken = prospectToken || token;
      if (!effectiveToken) {
        SweetAlertService.showError({
          title: "Error!",
          message:
            "Invalid or missing token. Please use a valid link to access the test.",
          onConfirm: () => { },
        });
        return;
      }
    }
    setHasStarted(true);
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!isSessionUrlFlow) {
      const effectiveToken = prospectToken || token;
      if (!effectiveToken) {
        SweetAlertService.showError({
          title: "Error!",
          message: "Invalid or missing token. Cannot submit the test.",
          onConfirm: () => { },
        });
        return;
      }
    }

    setIsSubmitting(true);
    console.log("🚀 SUBMITTING TEST...");

    // Build the choices array
    const choices = [];

    Object.keys(selections).forEach((step) => {
      const stepSelections = selections[step];
      Object.keys(stepSelections).forEach((questionId) => {
        const selection = stepSelections[questionId];
        if (selection && selection.value) {
          choices.push({
            selectedOptionId: selection.selectedOptionId,
            value: selection.value,
          });
        }
      });
    });

    const submitPayload = {
      choices: choices,
    };

    if (isSessionUrlFlow) {
      try {
        const resultAction = await dispatch(
          submitSessionUrl({
            company_session_id: Number(companySessionIdParam),
            name: sessionUrlUserData?.name || "",
            email: sessionUrlUserData?.email || "",
            phone: sessionUrlUserData?.phone || "",
            result_json: submitPayload,
          })
        );
        if (submitSessionUrl.rejected.match(resultAction)) {
          SweetAlertService.showError({
            title: "Error!",
            message: resultAction.payload || "Something went wrong. Please try again.",
            onConfirm: () => setIsSubmitting(false),
          });
        } else if (submitSessionUrl.fulfilled.match(resultAction)) {
          const result = resultAction.payload?.data?.result || resultAction.payload?.result || [];
          const lt = resultAction.payload?.data?.link_token || resultAction.payload?.link_token || null;
          SweetAlertService.showSuccess({
            title: "Success!",
            message: "Test has been submitted successfully.",
            onConfirm: () => {
              setIsSubmitting(false);
              if (Array.isArray(result) && result.length > 0) {
                setQuizResult(result);
                if (lt) setLinkToken(lt);
                setShowResults(true);
              }
            },
          });
        }
      } catch (error) {
        SweetAlertService.showError({
          title: "Error!",
          message: error?.message || "An unexpected error occurred. Please try again.",
          onConfirm: () => setIsSubmitting(false),
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    console.log("📤 FINAL SUBMISSION PAYLOAD:", { ...submitPayload, token });

    const effectiveToken = prospectToken || token;
    try {
      const resultAction = await dispatch(
        submitPersonalityTest({
          payload: submitPayload,
          token: effectiveToken,
        }),
      );

      console.log("📥 Result Action:", resultAction);

      if (submitPersonalityTest.fulfilled.match(resultAction)) {
        const responseData = resultAction.payload;

        console.log("✅ Response Data:", responseData);

        if (responseData?.result && responseData.result.length > 0) {
          console.log("✅ Setting quiz result:", responseData.result);
          setQuizResult(responseData.result);
          if (responseData?.link_token) setLinkToken(responseData.link_token);
          setShowResults(true);
        } else if (responseData?.message) {
          SweetAlertService.showError({
            title: "Error!",
            message: responseData.message,
            onConfirm: () => setIsSubmitting(false),
          });
        } else {
          SweetAlertService.showError({
            title: "Error!",
            message: "Something went wrong. Please try again.",
            onConfirm: () => setIsSubmitting(false),
          });
        }
      } else if (submitPersonalityTest.rejected.match(resultAction)) {
        const errorMessage =
          resultAction.payload ||
          "Failed to submit the test. Please try again.";
        console.error("❌ Submission failed:", errorMessage);

        SweetAlertService.showError({
          title: "Error!",
          message: errorMessage,
          onConfirm: () => setIsSubmitting(false),
        });
      }
    } catch (error) {
      console.error("❌ Submission error:", error);

      SweetAlertService.showError({
        title: "Error!",
        message:
          error?.message || "An unexpected error occurred. Please try again.",
        onConfirm: () => setIsSubmitting(false),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoHome = () => {
    setHasStarted(false);
    setCurrentStep(0);
    setShowResults(false);
    setQuizResult([]);
    setLinkToken(null);
    setSelections(() => {
      const obj = {};
      for (let i = 1; i <= TOTAL_STEPS; i++) obj[i] = {};
      return obj;
    });
    setCompleted([]);

    if (isSessionUrlFlow) {
      setProspectConfirmed(false);
      setSessionUrlUserData(null);
      return;
    }

    // Re-check token expiry when user goes home from prospect flow
    if (!prospectRequired) {
      dispatch(checkTokenExpiry(token));
    }
  };

  // ✅ 1. Check Loading State for Token
  if (tokenExpiryLoading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ThreeDotBouncingLoader />
        </div>
      </div>
    );
  }
  // If tokenExpiryData exists and is not empty, the token is invalid/used.
  if (
    tokenExpiryData?.token_valid == false &&
    tokenExpiryData?.expired == false
  ) {
    return <InvalidTokenPage />;
  }
  // ✅ 3. Check if Token is Expired (Data array has items)
  if (tokenExpiryData?.expired == true) {
    return <ExpiredTokenPage />;
  }

  // 3. Prospect Login View (if company params present and not confirmed)
  if (prospectRequired && !prospectConfirmed) {
    const headerOverrides = {
      title: (
        <>
          WHO ARE YOU <br />
          PERSONALITY ASSESSMENT <br />

        </>
      ),
      subtitle: " Discover Who You Are, What's Your Core Personality",
    };

    const initialValues = {
      company_id: Number(companyIdParam) || 0,
      company_name: companyNameParam || "",
    };

    return (
      <LoginFormFORprospect
        headerOverrides={headerOverrides}
        initialValues={initialValues}
        sessionType={sessionTypeParam}
        sessionName={sessionNameParam}
        companySessionId={companySessionIdParam}
        onSuccess={(payload) => {
          if (isSessionUrlFlow && payload?.userData) {
            setSessionUrlUserData(payload.userData);
          }
          setProspectConfirmed(true);
          const returnedToken = payload?.token || payload?.data?.token || null;
          if (returnedToken) {
            setProspectToken(returnedToken);
            try { localStorage.setItem('prospect_token', returnedToken); } catch (e) { }
          }
          dispatch(fetchPersonalityTestOptions());
        }}
      />
    );
  }

  // 3. Existing Loading/Submitting State
  if (loading || isSubmitting) {
    return (
      <div className="w-full min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <div className="mobile-responsive md-993:hidden">
          <div className="">
            <img
              src="./../assets/images/Logo-Incredible-Leaders.png"
              width={120}
              alt="Incredible Leaders"
            />
          </div>
          <div
            className={`mobile-menu ${isMobileMenuOpen ? "active" : ""}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            role="button"
            aria-label="Toggle menu"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }
            }}
          >
            <svg
              width="24"
              height="19"
              viewBox="0 0 32 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z"
                fill="black"
              />
              <path
                d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z"
                fill="black"
              />
              <path
                d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z"
                fill="black"
              />
            </svg>
          </div>
        </div>

        <div className="w-full flex gap-0">
          <Sidebar
            currentStep={currentStep}
            onSelectStep={handleSelectStep}
            completedSteps={completed}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
          <div className="flex-1 flex justify-center items-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8E1C] mx-auto"></div>
              <p className="mt-4 text-gray-600">
                {isSubmitting ? "Submitting your test..." : "Loading..."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show results screen
  if (showResults && quizResult.length > 0) {
    return (
      <div className="w-full min-h-screen bg-gray-50">
        {/* Mobile Header */}
        <div className="mobile-responsive md-993:hidden">
          <div className="">
            <img
              src="./../assets/images/Logo-Incredible-Leaders.png"
              width={120}
              alt="Incredible Leaders"
            />
          </div>
          <div
            className={`mobile-menu ${isMobileMenuOpen ? "active" : ""}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            role="button"
            aria-label="Toggle menu"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }
            }}
          >
            <svg
              width="24"
              height="19"
              viewBox="0 0 32 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z"
                fill="black"
              />
              <path
                d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z"
                fill="black"
              />
              <path
                d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z"
                fill="black"
              />
            </svg>
          </div>
        </div>

        <div className="w-full flex gap-0">
          <Sidebar
            currentStep={TOTAL_STEPS}
            onSelectStep={() => { }}
            completedSteps={[1, 2, 3, 4, 5, 6, 7, 8]}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />

          <div className="flex-1">
            <ResultCard
                quizResult={quizResult}
                onGoHome={handleGoHome}
                token={linkToken || prospectToken || token}
                isInterviewCandidate={isInterviewCandidate || isProspect}
              />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="mobile-responsive md-993:hidden">
        <div className="">
          <img
            src="./../assets/images/Logo-Incredible-Leaders.png"
            width={120}
            alt="Incredible Leaders"
          />
        </div>
        <div
          className={`mobile-menu ${isMobileMenuOpen ? "active" : ""}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          role="button"
          aria-label="Toggle menu"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }
          }}
        >
          <svg
            width="24"
            height="19"
            viewBox="0 0 32 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z"
              fill="black"
            />
            <path
              d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z"
              fill="black"
            />
            <path
              d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z"
              fill="black"
            />
          </svg>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="w-full flex gap-0">
        <Sidebar
          currentStep={currentStep}
          onSelectStep={handleSelectStep}
          completedSteps={completed}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        <div className="flex-1 p-6">
          {!hasStarted ? (
            <StartScreen onStart={handleStart} />
          ) : (
            <>
              <HeadingCard
                title={
                  <div className="flex justify-between items-center w-full">
                    <span className="font-lato not-italic font-bold text-[18px] md:text-[24px]  leading-[115%] text-white text-start">
                      Personality Assessment
                    </span>
                    <span className="font-lato not-italic font-bold text-[16px] md:text-[24px] leading-[115%] text-white">
                      Attempted Questions: {attemptedCount}/{totalQuestions}
                    </span>
                  </div>
                }
              >
                <StepComponent
                  step={currentStep}
                  questions={questions}
                  selected={selections[currentStep]}
                  onChange={handleChange(currentStep)}
                />
              </HeadingCard>

              <div className="flex justify-center items-center gap-4 pt-4 mt-4">
                <Button
                  onClick={handlePrev}
                  disabled={currentStep === 1}
                  className={`bg-[#FFF4E5] text-black px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] ${currentStep === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-[#FFF4E5] border border-[#FFF4E5] text-black "
                    }`}
                >
                  Previous
                </Button>

                <Button
                  onClick={
                    currentStep === TOTAL_STEPS ? handleSubmit : handleNext
                  }
                  disabled={
                    isSubmitting ||
                    Object.values(selections[currentStep] || {}).filter(
                      (v) => v !== null && v !== undefined,
                    ).length !== QUESTIONS_PER_STEP
                  }
                  className={`px-[18px] py-4 w-[150px] h-14 rounded-[12px] font-lato not-italic font-bold text-[16px] leading-[150%] transition-all duration-200 ${Object.values(selections[currentStep] || {}).filter(
                    (v) => v !== null && v !== undefined,
                  ).length === QUESTIONS_PER_STEP && !isSubmitting
                    ? "bg-[#FF8E1C] text-white hover:bg-[#E67E0C]"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                >
                  {currentStep === TOTAL_STEPS
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Next"}
                </Button>
              </div>

              <div className="mt-6">
                <Footer />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalityTest;
